import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, tap } from 'rxjs';
import {
  Group,
  CreateGroupRequest,
  UpdateGroupRequest,
  AssignGroupToAdminsRequest,
  CreateSubGroupRequest,
  RemoveUsersFromGroupsRequest,
  AddUsersToGroupsRequest
} from '../models/group.model';
import { PaginatedResponse } from '../models/api-response.model';
import { environment } from '../../../environments/environment';
import { CacheService } from './cache.service';

// DTO pour les admins
export interface AdminDTO {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  realmRoles: string[];
}

// DTO pour les realm roles
export interface RealmRoleDTO {
  id: string;
  name: string;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GroupService {
  private apiUrl = `${environment.apiUrl}/groups`;

  constructor(private http: HttpClient, private cacheService: CacheService) { }

  /**
   * Créer un nouveau groupe
   */
  createGroup(request: CreateGroupRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}`, request);
  }

  /**
   * Récupérer un groupe par son ID
   */
  getGroup(groupId: string): Observable<Group> {
    return this.http.get<Group>(`${this.apiUrl}/${groupId}`);
  }

  /**
   * Modifier un groupe
   */
  updateGroup(groupId: string, request: UpdateGroupRequest): Observable<any> {
    return this.http.put(`${this.apiUrl}/${groupId}`, request);
  }

  /**
   * Supprimer un groupe
   */
  deleteGroup(groupId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${groupId}`);
  }

  /**
   * Créer un sous-groupe
   */
  createSubGroup(parentGroupId: string, request: CreateSubGroupRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/${parentGroupId}/subgroups`, request);
  }

  /**
   * Récupérer tous les groupes (avec cache)
   */
  getAllGroups(first: number = 0, max: number = 20, search?: string, forceRefresh: boolean = false): Observable<any> {
    if (!forceRefresh && !search && this.cacheService.isCacheAvailable()) {
      return this.cacheService.getCachedGroups()!;
    }

    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<PaginatedResponse<Group>>(`${this.apiUrl}/all`, { params })
      .pipe(
        tap(response => {
          if (!search && !forceRefresh) {
            this.cacheService.setCache(response);
          }
        }),
        catchError(error => {
          console.error('Erreur de chargement des groupes:', error);
          if (!search && this.cacheService.isCacheAvailable()) {
            return this.cacheService.getCachedGroups()!;
          }
          throw error;
        })
      );
  }

  /**
   * Méthode pour forcer l'invalidation du cache
   */
  invalidateGroupsCache(): void {
    this.cacheService.forceRefresh();
  }

  /**
   * Récupérer les groupes gérés par l'admin connecté
   */
  getMyManagedGroups(): Observable<any> {
    let params = new HttpParams();
    return this.http.get<PaginatedResponse<Group>>(`${this.apiUrl}/my-managed-groups`, { params });
  }

  /**
 * Ajouter des utilisateurs à des groupes
 */
  addUsersToGroups(request: AddUsersToGroupsRequest): Observable<any> {
    // console.log('📤 [GroupService] Envoi requête addUsersToGroups:', request);

    // Vérifier que la requête a le bon format
    if (!request.userIds || !request.groupIds) {
      throw new Error('Format de requête invalide: userIds et groupIds sont requis');
    }

    return this.http.post(`${this.apiUrl}/members/add`, request)
      .pipe(
        tap(response => console.log('✅ [GroupService] Réponse addUsersToGroups:', response)),
        catchError(error => {
          console.error('❌ [GroupService] Erreur addUsersToGroups:', error);
          throw error;
        })
      );
  }

  /**
 * Retirer des utilisateurs de groupes
 */
  removeUsersFromGroups(request: RemoveUsersFromGroupsRequest): Observable<any> {
    // console.log('📤 [GroupService] Envoi requête removeUsersFromGroups:', request);

    // Vérifier que la requête a le bon format
    if (!request.userIds || !request.groupIds) {
      throw new Error('Format de requête invalide: userIds et groupIds sont requis');
    }

    return this.http.request('DELETE', `${this.apiUrl}/members/remove`, {
      body: request,
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    })
      .pipe(
        tap(response => console.log('✅ [GroupService] Réponse removeUsersFromGroups:', response)),
        catchError(error => {
          console.error('❌ [GroupService] Erreur removeUsersFromGroups:', error);
          throw error;
        })
      );
  }

  /**
   * Récupérer les membres d'un groupe avec pagination
   */
  getGroupMembers(groupId: string, first: number = 0, max: number = 20): Observable<PaginatedResponse<any>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    return this.http.get<any[]>(`${this.apiUrl}/${groupId}/members`, { params })
      .pipe(
        map((users: any[]) => {
          // Transforme le tableau simple en structure paginée
          return {
            content: users,
            totalElements: users.length,
            totalPages: 1,
            size: users.length,
            number: 0,
            first: true,
            last: true
          } as PaginatedResponse<any>;
        }),
        catchError(error => {
          console.error('❌ Erreur récupération membres:', error);
          throw error;
        })
      );
  }

  /**
 * Récupérer TOUS les membres d'un groupe (sans pagination)
 */
  getAllGroupMembers(groupId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${groupId}/members`);
  }

  /**
   * Récupérer tous les admins d'un groupe spécifique
   */
  getGroupAdmins(groupId: string): Observable<AdminDTO[]> {
    // console.log('📡 [GroupService] Appel getGroupAdmins pour groupId:', groupId);
    return this.http.get<AdminDTO[]>(`${this.apiUrl}/${groupId}/admins`)
      .pipe(
        // tap(admins => console.log('✅✅✅ Admins récupérés pour le groupe:', admins)),
        catchError(error => {
          // console.error('❌❌❌ Erreur récupération admins:', error);
          throw error;
        })
      );
  }

  /**
   * Assigner un groupe à des admins (SUPER_ADMIN uniquement)
   */
  assignGroupToAdmins(groupId: string, request: AssignGroupToAdminsRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/${groupId}/admins`, request)
      .pipe(
        tap(() => console.log('✅ Admins assignés avec succès')),
        catchError(error => {
          // console.error('❌ Erreur assignation admins:', error);
          throw error;
        })
      );
  }

  /**
   * Retirer un admin d'un groupe (SUPER_ADMIN uniquement)
   */
  removeAdminFromGroup(groupId: string, adminId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${groupId}/admins/${adminId}`)
      .pipe(
        tap(() => console.log('✅ Admin retiré avec succès')),
        catchError(error => {
          console.error('❌ Erreur retrait admin:', error);
          throw error;
        })
      );
  }

  /**
   * Récupérer les realm roles d'un utilisateur
   */
  getUserRealmRoles(userId: string): Observable<RealmRoleDTO[]> {
    return this.http.get<RealmRoleDTO[]>(`${this.apiUrl}/users/${userId}/realm-roles`);
  }

  /**
   * Récupérer les groupes d'un admin spécifique
   */
  getGroupsByAdmin(adminId: string): Observable<Group[]> {
    return this.http.get<Group[]>(`${this.apiUrl}/by-admin/${adminId}`);
  }

  /**
   * Rechercher des groupes
   */
  searchGroups(term: string): Observable<Group[]> {
    let params = new HttpParams().set('term', term);
    return this.http.get<Group[]>(`${this.apiUrl}/search`, { params });
  }

  /**
   * Récupérer la hiérarchie d'un groupe
   */
  getGroupHierarchy(groupId: string): Observable<Group[]> {
    return this.http.get<Group[]>(`${this.apiUrl}/${groupId}/hierarchy`);
  }

  /**
   * Récupérer les utilisateurs d'un groupe spécifique par son nom
   */
  getUsersByGroupName(groupName: string, first: number = 0, max: number = 20): Observable<PaginatedResponse<any>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    return this.http.get<PaginatedResponse<any>>(
      `${environment.apiUrl}/users/groups/${groupName}/users`,
      { params }
    );
  }

  /**
   * Récupérer uniquement les groupes racine
   */
  getRootGroupsOld(first: number = 0, max: number = 20, search?: string): Observable<PaginatedResponse<Group>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString())
      .set('rootOnly', 'true');

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<PaginatedResponse<Group>>(`${this.apiUrl}/root`, { params });
  }

  // dans group.service.ts

  /**
   * Récupère tous les groupes racines (niveau 1) formatés "nom (sigle)"
   */
  getRootGroups(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/root-groups`);
  }

  /**
   * Récupère les sous-groupes d'un groupe parent identifié par son sigle
   * @param sigle Sigle du groupe parent
   */
  getSubgroupsByParentSigle(sigle: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/subgroups-by-sigle/${sigle}`);
  }
}